/**
 * Microsoft Clarity loader — privacy-aware, env-driven.
 *
 * What it does:
 *   1. Fetches GET /api/config/public to discover the Clarity Project ID
 *      the backend exposes (CLARITY_PROJECT_ID in /app/backend/.env).
 *   2. If the ID is non-empty AND the user is NOT in stealth mode AND
 *      we're not in a server-side render context, injects the standard
 *      Clarity loader script tag.
 *   3. Does nothing in dev unless explicitly enabled, so we don't
 *      pollute Clarity dashboards with developer traffic.
 *
 * Why fetch the ID from /api/config/public instead of REACT_APP_*:
 *   CRA env vars are baked at build time — flipping Clarity on/off
 *   would require a rebuild + redeploy. Reading from /api lets the
 *   founder set the env var in backend/.env and just restart the
 *   backend → Clarity wakes up on every NEW visitor session.
 *
 * Stealth-mode contract: this loader respects the SAME on/off switch
 * as GA4/GTM/TikTok. If the user has stealth enabled (or hasn't yet
 * dismissed the consent gate), Clarity is NEVER injected. The founder
 * relies on this guarantee for the privacy promise on the marketing
 * site.
 */
const SCRIPT_ID = "trubeaute-clarity-loader";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const _alreadyInjected = () => !!document.getElementById(SCRIPT_ID);

const _isLocalhost = () =>
  typeof window !== "undefined" &&
  /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

/**
 * Boot Clarity if all gates pass.
 *
 * @param {object}  opts
 * @param {boolean} opts.stealthOn - true when StealthContext says "block trackers"
 * @param {boolean} [opts.allowLocalhost=false] - opt-in for dev testing
 */
export const bootClarity = async ({
  stealthOn,
  allowLocalhost = false,
} = {}) => {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (_alreadyInjected()) return;
  if (stealthOn) return;
  if (_isLocalhost() && !allowLocalhost) return;
  // index.html now boots Clarity inline (with the same stealth check),
  // so once `window.clarity` is a function we know it's already
  // initialised and we must not double-inject. Founder set the ID
  // directly in /app/frontend/public/index.html on Feb 9, 2026 so
  // Clarity ships without needing the backend env var to be set in
  // production. This runtime loader stays as a defensive fallback
  // for old HTML snapshots / future env-driven re-enables.
  if (typeof window.clarity === "function") return;

  let projectId = "";
  try {
    const r = await fetch(`${BACKEND_URL}/api/config/public`, {
      credentials: "omit",
      headers: { Accept: "application/json" },
    });
    if (!r.ok) return;
    const j = await r.json();
    projectId = (j && j.clarity_project_id) || "";
  } catch {
    // Backend unreachable — silently skip. Clarity is a nice-to-have.
    return;
  }
  if (!projectId) return;   // Founder hasn't set CLARITY_PROJECT_ID yet.

  // Standard Clarity boot snippet, adapted to set a stable script id
  // so we can detect re-mount + avoid double-injection.
  const s = document.createElement("script");
  s.id = SCRIPT_ID;
  s.async = true;
  s.text = `
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${projectId}");
  `;
  document.head.appendChild(s);
};
